import { useMemo } from 'react'

import { F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import {
  AtsVacancySelectorDataDocument,
  AtsVacancySelectorItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  jobPostingId: string
  value?: string
  onChange: (vacancyId: string) => void
}

export const VacancySelector = ({ jobPostingId, value, onChange }: Props) => {
  const { employee: sessionEmployee } = useCurrentSession()

  const { loading, data } = useQuery(AtsVacancySelectorDataDocument)

  const vacancies = useMemo(() => data?.ats.vacanciesConnection.nodes ?? [], [data])

  const options: F0SelectItemProps<string>[] = useMemo(() => {
    const toOption = (vacancy: AtsVacancySelectorItemFragment): F0SelectItemProps<string> => {
      const description =
        [
          vacancy.location?.name,
          [vacancy.jobCatalogTreeRole?.name, vacancy.jobCatalogTreeLevel?.name]
            .filter(Boolean)
            .join(' ') || undefined,
          vacancy.team?.name,
        ]
          .filter(Boolean)
          .join(' / ') || undefined

      return {
        value: String(vacancy.id),
        label: vacancy.name,
        description,
        tag: vacancy.lineManager?.fullName,
      }
    }

    const assignedToMe: AtsVacancySelectorItemFragment[] = []
    const allVacancies: AtsVacancySelectorItemFragment[] = []

    for (const vacancy of vacancies) {
      if (vacancy.atsVacancyHiringManagers.some((hm) => hm.id === sessionEmployee.id)) {
        assignedToMe.push(vacancy)
      } else {
        allVacancies.push(vacancy)
      }
    }

    const sortByJobPosting = (
      a: AtsVacancySelectorItemFragment,
      b: AtsVacancySelectorItemFragment
    ) => {
      const aLinked = a.jobPosting?.id === jobPostingId ? 0 : 1
      const bLinked = b.jobPosting?.id === jobPostingId ? 0 : 1
      return aLinked - bLinked
    }

    assignedToMe.sort(sortByJobPosting)
    allVacancies.sort(sortByJobPosting)

    const result: F0SelectItemProps<string>[] = []

    if (assignedToMe.length > 0) {
      result.push({
        value: '__assigned_to_me_header__',
        label: i18n.t('ats.vacancy_selector.assigned_to_me'),
        disabled: true,
      })
      result.push(...assignedToMe.map(toOption))
    }

    if (allVacancies.length > 0) {
      if (assignedToMe.length > 0) {
        result.push({ type: 'separator' })
      }
      result.push({
        value: '__all_vacancies_header__',
        label: i18n.t('ats.vacancy_selector.all_vacancies'),
        disabled: true,
      })
      result.push(...allVacancies.map(toOption))
    }

    return result
  }, [vacancies, sessionEmployee.id, jobPostingId])

  return (
    <F0Select
      label={i18n.t('ats.vacancy_selector.select_label')}
      name="vacancy"
      placeholder={i18n.t('ats.vacancy_selector.select_placeholder')}
      size="md"
      value={value}
      onChange={(val: string) => onChange(val)}
      options={options}
      showSearchBox
      searchBoxPlaceholder={i18n.t('ats.vacancy_selector.search_placeholder')}
      searchEmptyMessage={i18n.t('ats.vacancy_selector.search_no_results')}
      readonly={loading}
    />
  )
}
